import classnames from "classnames";
import React, { useContext, useEffect } from "react";
import { RicosContext } from "./../../ricos-context/index.js";
import { Node_Type } from "./../../ricos-schema/legacy/wix/rich_content/v1/index.js";
import { isDefined } from "./../../wix-rich-content-common/index.js";
import { DoubleLoader } from "./../../ui-components/index.js";
import {
  FILE_UPLOAD_EXTENSION,
  FILE_UPLOAD_LINK,
  FILE_UPLOAD_NAME,
  FILE_UPLOAD_NAME_CONTAINER,
  ROOT
} from "../data-hooks.js";
import { DownloadIcon } from "../icons/download-icon.js";
import { ErrorIcon } from "../icons/error-icon.js";
import { getIcon } from "../icons/get-icon.js";
import styles from "../statics/styles/file-upload-viewer-scss.js";
import { FILE_UPLOAD_TYPE } from "../types.js";
const KB = 1e3;
const MB = KB * 1e3;
const getNameWithoutType = (fileName) => {
  if (!fileName) {
    return fileName;
  }
  const lastDotIndex = fileName.lastIndexOf(".");
  return lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;
};
const getFormattedFileSize = (componentData, tempDataPlaceHolder) => {
  const sizeInKb = componentData.sizeInKb ?? tempDataPlaceHolder?.sizeInKb;
  const size = componentData.size ?? tempDataPlaceHolder?.size;
  if (sizeInKb && !isNaN(parseFloat(sizeInKb))) {
    return formatFileSize(parseFloat(sizeInKb) * KB);
  }
  if (size) {
    return formatFileSize(size);
  }
  return void 0;
};
const formatFileSize = (sizeInBytes) => {
  if (sizeInBytes < KB) {
    return `${Math.round(sizeInBytes)}B`;
  }
  if (sizeInBytes < MB) {
    return `${Math.round(sizeInBytes / KB)}KB`;
  }
  return `${(sizeInBytes / MB).toFixed(2)}MB`;
};
const FileViewerContent = ({ state, fileData }) => {
  const { t } = useContext(RicosContext);
  const { name, type } = fileData;
  if (!isDefined(name) || !isDefined(type)) {
    return null;
  }
  const TypeIcon = getIcon(type);
  const fileBasename = getNameWithoutType(name);
  const fileExtension = type ? `.${type}` : void 0;
  let infoString;
  if (state === "error") {
    infoString = t("UploadFile_Error_Generic_Item");
  } else {
    const translationKey = state === "loading" ? "UploadFile_Viewer_Loader" : "UploadFile_Viewer_Download";
    const fileType = type?.toUpperCase() ?? "";
    const formattedFileSize = getFormattedFileSize(fileData);
    const fileSizeSuffix = formattedFileSize ? ` • ${formattedFileSize}` : "";
    infoString = `${t(translationKey, { fileType })}${fileSizeSuffix}`;
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(TypeIcon, { className: styles.file_upload_type_icon }), /* @__PURE__ */ React.createElement("div", { className: styles.file_upload_description }, /* @__PURE__ */ React.createElement("div", { className: styles.file_upload_name_container, "data-hook": FILE_UPLOAD_NAME_CONTAINER }, /* @__PURE__ */ React.createElement("span", { className: styles.file_upload_name, "data-hook": FILE_UPLOAD_NAME }, fileBasename), fileExtension && /* @__PURE__ */ React.createElement("span", { className: styles.file_upload_extension, "data-hook": FILE_UPLOAD_EXTENSION }, fileExtension)), /* @__PURE__ */ React.createElement("div", { className: styles.file_upload_file_info }, infoString)), /* @__PURE__ */ React.createElement("div", { className: styles.file_upload_status_icon_container }, state === "loading" && /* @__PURE__ */ React.createElement(DoubleLoader, { className: styles.file_loader_icon }), state === "error" && /* @__PURE__ */ React.createElement(ErrorIcon, { className: styles.file_upload_status_icon }), state === "ready" && /* @__PURE__ */ React.createElement(DownloadIcon, { className: styles.file_upload_status_icon })));
};
const FileViewer = ({ settings, componentData, isLoading = false, tempDataPlaceHolder, helpers, nodeId }) => {
  const { tabIndex } = useContext(RicosContext);
  const linkRef = React.useRef(null);
  const [shouldDownloadOnRender, setShouldDownloadOnRender] = React.useState(false);
  const [resolvedFileUrl, setResolvedFileUrl] = React.useState(void 0);
  const [isResolvingUrl, setResolvingUrl] = React.useState(false);
  const state = isLoading || isResolvingUrl ? "loading" : componentData.error ? "error" : "ready";
  useEffect(() => {
    if (!shouldDownloadOnRender) {
      return;
    }
    linkRef.current?.click();
    setShouldDownloadOnRender(false);
  }, [shouldDownloadOnRender]);
  const onFileClick = () => {
    if (componentData.name) {
      helpers?.onViewerAction?.(FILE_UPLOAD_TYPE, "Click", componentData.name, nodeId, {
        type: Node_Type.FILE,
        fileData: componentData
      });
    }
  };
  const renderContainerWithoutLink = () => {
    return /* @__PURE__ */ React.createElement("div", { className: styles.file_upload_link }, /* @__PURE__ */ React.createElement(FileViewerContent, { state, fileData: tempDataPlaceHolder || componentData }));
  };
  const renderDownloadLink = (fileUrl) => {
    return /* @__PURE__ */ React.createElement(
      "a",
      {
        "data-hook": FILE_UPLOAD_LINK,
        "data-cancel-link": true,
        ref: linkRef,
        className: styles.file_upload_link,
        tabIndex,
        href: fileUrl,
        target: settings.downloadTarget,
        download: true,
        onClick: onFileClick
      },
      /* @__PURE__ */ React.createElement(FileViewerContent, { state, fileData: componentData })
    );
  };
  const renderResolverButton = () => {
    const handleClick = () => {
      if (isResolvingUrl) {
        return;
      }
      setResolvingUrl(true);
      settings.resolveFileUrl({
        id: componentData?.src?.id,
        type: componentData.type,
        path: componentData.path,
        mimeType: componentData.mimeType,
        name: componentData.name,
        size: componentData.size,
        pdfSettings: componentData.pdfSettings,
        privacy: componentData?.src?.private ? "private" : "public"
      }).then((resolvedFileUrl2) => {
        setResolvedFileUrl(resolvedFileUrl2);
        setResolvingUrl(false);
        setShouldDownloadOnRender(true);
      });
    };
    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        handleClick();
      }
    };
    return /* @__PURE__ */ React.createElement(
      "button",
      {
        type: "button",
        onClick: handleClick,
        onKeyDown: handleKeyDown,
        className: styles.file_upload_link
      },
      /* @__PURE__ */ React.createElement(FileViewerContent, { state, fileData: componentData })
    );
  };
  const renderContent = () => {
    if (componentData.error || tempDataPlaceHolder) {
      return renderContainerWithoutLink();
    }
    const baseFileUrl = componentData.src?.url || resolvedFileUrl;
    const fileUrl = baseFileUrl && componentData.type !== "pdf" ? `${baseFileUrl}${baseFileUrl.includes("?") ? "&" : "?"}dn=${encodeURIComponent(
      componentData.name || ""
    )}` : baseFileUrl;
    return fileUrl ? renderDownloadLink(fileUrl) : renderResolverButton();
  };
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      "data-hook": ROOT,
      className: classnames(
        styles.file_upload_container,
        componentData.error && styles.file_upload_error_container
      )
    },
    renderContent()
  );
};
export {
  FileViewer
};
//# sourceMappingURL=file-viewer.js.map
